import React, {useMemo} from 'react'
import {StyleSheet, View} from 'react-native'
import {
  type AppBskyFeedDefs,
  AppBskyFeedPost,
  AppBskyEmbedVideo,
  moderatePost,
} from '@atproto/api'

import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {type Shadow, usePostShadow, POST_TOMBSTONE} from '#/state/cache/post-shadow'
import {ContentHider} from '#/components/moderation/ContentHider'
import {PreviewableUserAvatar} from '#/view/com/util/UserAvatar'
import {Text} from '#/view/com/util/text/Text'
import {atoms as a} from '#/alf'
import {usePalette} from '#/lib/hooks/usePalette'
import {VideoPlayer} from './VideoPlayer'

export function VideoPost({
  post,
}: {
  post: AppBskyFeedDefs.PostView
}) {
  const moderationOpts = useModerationOpts()

  const record = useMemo(
    () => {
      if (AppBskyFeedPost.isRecord(post.record)) {
        return post.record
      }
      return undefined
    },
    [post],
  ) as AppBskyFeedPost.Record | undefined
  const postShadowed = usePostShadow(post)
  const moderation = useMemo(
    () => (moderationOpts ? moderatePost(post, moderationOpts) : undefined),
    [moderationOpts, post],
  )

  if (postShadowed === POST_TOMBSTONE) {
    return null
  }

  if (!record || !moderation) {
    return null
  }

  return (
    <VideoPostInner
      post={postShadowed}
      moderation={moderation}
    />
  )
}

function VideoPostInner({
  post,
  moderation,
}: {
  post: Shadow<AppBskyFeedDefs.PostView>
  moderation: any
}) {
  const pal = usePalette('default')

  // Only render if post has a video embed
  if (!post.embed || !AppBskyEmbedVideo.isView(post.embed)) {
    return null
  }

  return (
    <View style={styles.container}>
      {/* Author info above the video */}
      <View style={styles.authorContainer}>
        <PreviewableUserAvatar
          size={32}
          profile={post.author}
          moderation={moderation.ui('avatar')}
          type={post.author.associated?.labeler ? 'labeler' : 'user'}
        />
        <View style={styles.authorTextContainer}>
          <Text style={[styles.authorName, pal.text]} numberOfLines={1}>
            {post.author.displayName || post.author.handle}
          </Text>
          <Text style={[styles.authorHandle, pal.textLight]} numberOfLines={1}>
            @{post.author.handle}
          </Text>
        </View>
      </View>

      {/* Video */}
      <View style={styles.videoContainer}>
        <ContentHider
          modui={moderation?.ui('contentMedia')}
          style={styles.contentHider}
          childContainerStyle={styles.contentHiderChild}>
          <VideoPlayer embed={post.embed} />
        </ContentHider>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    width: 400,
    backgroundColor: '#000',
    paddingLeft: 10,
    paddingRight: 10,
  },
  authorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    gap: 8,
    backgroundColor: '#000',
  },
  authorTextContainer: {
    flex: 1,
    gap: 2,
  },
  authorName: {
    fontSize: 14,
    fontWeight: '600',
  },
  authorHandle: {
    fontSize: 12,
  },
  videoContainer: {
    width: 380,
    height: 600,
    backgroundColor: '#000',
  },
  contentHider: {
    flex: 1,
  },
  contentHiderChild: {
    flex: 1,
  },
})
